import { useMemo } from 'react';
import { usePastAnnualRevenueEarnings } from '../usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import {
  CreateNarrativeFuturePEMultipleCalculator,
  FuturePEMultipleContext,
  hasOnlyFairValue,
  useFuturePEMultipleCalculation,
  ViewEditNarrativeFuturePEMultipleCalculator,
  calculateValuationInitialisation,
} from '../../components/Calculators';
import { isLatestPublishedUpdateLocked } from '../../components/PublicNarrativeEdit/utils';
import type { Narrative } from '../useNarrative';
import type {
  NarrativeCompanyLatestPublishedUpdate,
  NarrativeCompanyDraftUpdate,
  MyNarrativeCompany,
} from '../useNarrativeCompany';

type Props = {
  latestPublishedUpdate?: NarrativeCompanyLatestPublishedUpdate;
  draftUpdate?: NarrativeCompanyDraftUpdate;
  company?: MyNarrativeCompany['Company'] | Narrative['company'];
};

export const useNarrativeValuationManager = ({
  latestPublishedUpdate,
  draftUpdate,
  company,
}: Props) => {
  if (!company) {
    throw new Error("Narrative's company is missing");
  }

  const valuation = useMemo(() => {
    if (
      latestPublishedUpdate &&
      !isLatestPublishedUpdateLocked(latestPublishedUpdate)
    ) {
      return latestPublishedUpdate.valuation;
    }

    if (draftUpdate && draftUpdate.valuation) {
      return draftUpdate.valuation;
    }

    return null;
  }, [latestPublishedUpdate, draftUpdate]);

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: valuation?.fiscalData ?? null,
  });

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        !valuation || hasOnlyFairValue(valuation)
          ? new CreateNarrativeFuturePEMultipleCalculator(company)
          : new ViewEditNarrativeFuturePEMultipleCalculator(
              valuation,
              company.analysisValue?.marketCapUSD ?? 0
            )
      ),
    [company, valuation]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
  } = useFuturePEMultipleCalculation(strategyContext);

  return {
    valuation,
    pastAnnualRevenueEarnings,
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
    calculateValuationInitialisation,
  };
};
